import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import React from 'react';

import { z } from 'zod';

import { IconRegistry } from '../registries/IconRegistry';
import type { CustomRendererComponentProps, ToolExecutionContext } from '../types';
import { Tool } from './Tool';

import { MediaSearchSourceEnum } from 'js/src/client';
import { ApiService } from 'js/src/client/services.gen';
import BackgroundAsset from 'js/src/components/app/BackgroundAsset';
import { MEDIA_LICENSE_OPTIONS } from 'js/src/data/media';
import { selectMediaOptions } from 'js/src/data/media-recommendation-preferences';

const RESULT_COUNT = 10;

export const iconSearchSchema = z.object({
    query: z
        .string()
        .describe(
            'The search query to find relevant icons. Works best with detailed queries. Must be English.',
        ),
});
export type IconSearchArgs = z.infer<typeof iconSearchSchema>;

interface IconRepresentation {
    id: string;
    type: string;
    title: string;
    tags: string;
}

export class IconSearchTool extends Tool<typeof iconSearchSchema> {
    protected override readonly schema = iconSearchSchema;
    protected override readonly toolName = TOOL_NAMES.searchIcons;
    public override readonly subagentAccess = SubagentAccess.READ_ONLY;
    protected override readonly toolDescription = 'Search for icon assets using a text query';

    public override canRunAsync = true;
    public override customRenderer = IconSearchResultRenderer;

    public override getToolDisplayName(): string {
        return 'Searching for icons';
    }

    public get statusIcon(): string {
        return 'emoji_symbols';
    }

    public async execute(args: IconSearchArgs, context: ToolExecutionContext) {
        const { query } = args;

        if (!selectMediaOptions(this._getState()).stock_icons_enabled) {
            return `Stock icons are disabled by the user's media preferences. Use stock media with ${TOOL_NAMES.searchMedia} instead.`;
        }

        try {
            // Icon searches use specific filter and source
            const filter = MEDIA_LICENSE_OPTIONS.EDITORIAL + ',' + MEDIA_LICENSE_OPTIONS.STOCK;
            const source = MediaSearchSourceEnum.ICON_LIBRARY;

            const response = await ApiService.mediaSearchCreate({
                body: {
                    q: query,
                    search_source: source,
                    page: 1,
                    count: RESULT_COUNT,
                    filter,
                    autocorrect: false,
                    did_you_mean: false,
                },
            });

            // Add results to IconRegistry and return representations
            const iconRegistry = IconRegistry.getInstance();
            const representations = response.data.results.map((result) => {
                const id = iconRegistry.addItem(result);
                return iconRegistry.getRepresentation(id);
            });

            return JSON.stringify(representations);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error searching for icons: ${errorMessage}`;
        }
    }
}

const IconSearchResultRenderer: React.FC<CustomRendererComponentProps> = ({
    isLoading,
    result,
    toolCall,
}) => {
    if (isLoading) {
        return null;
    }
    const iconRegistry = IconRegistry.getInstance();

    let parsedResult;
    try {
        parsedResult = JSON.parse(result);
    } catch (error) {
        return <div>{result}</div>;
    }

    const representations = parsedResult || [];
    const args = toolCall ? JSON.parse(toolCall.function.arguments) : {};
    const query = args.query || 'Unknown query';

    return (
        <div>
            Found {representations.length} icons for &quot;{query}&quot;
            <div className="row g-2 asset-container">
                {representations.slice(0, 6).map((rep: IconRepresentation, index: number) => {
                    try {
                        const fullData = iconRegistry.getItem(rep.id);
                        if (!fullData) {
                            return (
                                <div key={index} className="col-md-6">
                                    ID: {rep.id} - {rep.type}: {rep.title}
                                </div>
                            );
                        }

                        return React.createElement(BackgroundAsset, {
                            key: index,
                            searchContext: null,
                            media: fullData,
                            media_id: index,
                            result_index: index,
                        });
                    } catch (error) {
                        return (
                            <div key={index} className="col-md-6">
                                ID: {rep.id} - {rep.type}: {rep.title}
                            </div>
                        );
                    }
                })}
            </div>
            {representations.length > 6 && (
                <div>... and {representations.length - 6} more results</div>
            )}
        </div>
    );
};
